"use client";

import { Dispatch, SetStateAction, useEffect } from "react";
import { AnimatePresence, motion, TargetAndTransition } from "motion/react";
import { X } from "lucide-react";
import { cn } from "@/lib/utils";

interface ModalProps {
  children: React.ReactNode;
  className?: string;
  showModal?: boolean;
  setShowModal?: Dispatch<SetStateAction<boolean>> | ((open: boolean) => void);
  onClose?: () => void;
  desktopOnly?: boolean;
  preventDefaultClose?: boolean;
}

export const dialogOpenState: TargetAndTransition = {
  opacity: 1,
  filter: "blur(0px)",
  rotateX: 0,
  rotateY: 0,
  z: 0,
  transition: {
    delay: 0.1,
    duration: 0.4,
    ease: [0.17, 0.67, 0.51, 1],
    opacity: {
      delay: 0.1,
      duration: 0.4,
      ease: "easeOut",
    },
  },
};

export const dialogInitialState: TargetAndTransition = {
  opacity: 0,
  filter: "blur(10px)",
  z: -100,
  rotateY: 25,
  rotateX: 5,
  transformPerspective: 500,
  transition: {
    duration: 0.3,
    ease: [0.67, 0.17, 0.62, 0.64],
  },
};

function DialogContent({
  children,
  close,
  className,
}: {
  children: React.ReactNode;
  close: () => void;
  className?: string;
}) {
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        close();
      }
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [close]);

  // Prevent background scrolling when modal is open
  useEffect(() => {
    const originalStyle = window.getComputedStyle(document.body).overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = originalStyle;
    };
  }, []);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop overlay */}
      <motion.div
        key="modal-backdrop"
        className="fixed inset-0 bg-black/60 backdrop-blur-sm"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        onClick={close}
      />

      {/* Modal Dialog with Motion 3D transitions */}
      <motion.div
        key="modal-content"
        role="dialog"
        aria-modal="true"
        initial={dialogInitialState}
        animate={dialogOpenState}
        exit={dialogInitialState}
        style={{ transformPerspective: 500 }}
        className={cn(
          "relative z-10 w-full max-w-[420px] rounded-2xl border border-border bg-card text-card-foreground p-6 sm:p-8 shadow-2xl overflow-hidden",
          className
        )}
      >
        {children}
        <button
          className="absolute right-4 top-4 rounded-full p-2 text-muted-foreground hover:bg-muted hover:text-foreground transition-colors cursor-pointer"
          aria-label="Close"
          onClick={close}
        >
          <X className="size-4" />
        </button>
      </motion.div>
    </div>
  );
}

export function Modal({
  children,
  className,
  showModal,
  setShowModal,
  onClose,
  preventDefaultClose,
}: ModalProps) {
  const closeModal = () => {
    if (preventDefaultClose) return;
    onClose && onClose();
    if (setShowModal) {
      setShowModal(false);
    }
  };

  return (
    <AnimatePresence>
      {showModal ? (
        <DialogContent close={closeModal} className={className}>
          {children}
        </DialogContent>
      ) : null}
    </AnimatePresence>
  );
}
